/**
 * The wire contract, from `fig/Fig-API-Reference.md`.
 *
 * Every enum below is the COMPLETE value set, read from `Fig.Domain/Enums` and confirmed
 * against the live deployment. This matters more than it looks: the reference's own history
 * records a client author sending `"Daily"` for a sync schedule - a plausible value that does
 * not exist - and getting a model-binding 400 that reads like a serialization fault. An
 * example value is not a specification.
 */

/** Enums cross the wire as NAMES. Integers are accepted on input; never send them. */
export const AGENCY_MODES = ['Direct', 'Agency'] as const;
export type AgencyMode = (typeof AGENCY_MODES)[number];

export const USER_ROLES = ['Admin', 'Individual', 'QaReviewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Exactly two. There is no `Daily`. */
export const SYNC_SCHEDULES = ['Every15Minutes', 'Hourly'] as const;
export type SyncSchedule = (typeof SYNC_SCHEDULES)[number];

export const SYNC_STATUSES = ['Ok', 'Failed', 'Syncing'] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

export const SENTIMENTS = ['Positive', 'Neutral', 'Negative'] as const;
export type Sentiment = (typeof SENTIMENTS)[number];

export const TRANSCRIPT_ROLES = ['Agent', 'User'] as const;
export type TranscriptRole = (typeof TRANSCRIPT_ROLES)[number];

/** The role claim on Fig's own token - lowercased, and NOT the same strings as `UserRole`. */
export const ROLE_CLAIMS = ['admin', 'individual', 'qareviewer', 'client_portal'] as const;
export type RoleClaim = (typeof ROLE_CLAIMS)[number];

export const isPortalRole = (r: RoleClaim): boolean => r === 'client_portal';
export const isAgencyAdmin = (r: RoleClaim): boolean => r === 'admin';

// ---------------------------------------------------------------- entities

export interface SessionResponse {
  readonly token: string;
  readonly role: RoleClaim;
  readonly expiresInSeconds: number;
  /**
   * Whether this person may switch the app between environments at runtime.
   *
   * **A boolean, never a URL.** The environments are a closed set compiled into the client, so
   * the server says WHO may switch and never WHERE TO — which is what stops a compromised or
   * mis-seeded response from re-pointing a real person's app somewhere it should not be.
   *
   * Deliberately not a claim in the token: it is re-read on every exchange, exactly like the
   * disabled check, so clearing the flag takes effect within one token lifetime rather than
   * whenever somebody next signs in.
   *
   * **Optional on the wire.** An older API that does not send it, or a Client Portal response,
   * leaves it undefined — which reads as false everywhere it is used. The safe answer is the one
   * you get by omission.
   */
  readonly isInternal?: boolean;
  /**
   * Whether this is the read-only "have a look around" tour rather than a real account.
   *
   * Optional and defaulted to false everywhere it is read, like `isInternal`: an older API, or a
   * Client Portal response, simply does not say — and the safe thing to conclude from not being
   * told is that this is a real account, because that is the state with no sample-data band and
   * no special handling.
   */
  readonly isDemo?: boolean;
  /**
   * When this account is due to be deleted, ISO-8601, or absent when none is scheduled.
   *
   * **Absent is the normal case and the safe default**, like the two flags above. Its presence is
   * what tells the app to open the restore screen rather than the calls list — a person who asked
   * to delete their account and then sees the product behaving normally concludes the deletion
   * did not work.
   *
   * Signing in during the window is deliberately still allowed: the recovery period exists so
   * somebody can change their mind, and somebody who cannot get in cannot. The API refuses
   * everything except the restore path.
   */
  readonly deletionDueAt?: string;
}

export interface Branding {
  readonly displayName: string;
  /**
   * A 30-minute SAS URL, minted per request. **Render it; never persist it.** Caching the URL
   * caches a link that expires; the image behind it never changes. See finding S6.
   */
  readonly logoUrl: string | null;
  readonly accentColor: string | null;
}

/**
 * An UNSIGNED blob path, not a URL. Branded so it cannot be handed to an `<img src>` or an
 * `Image` source without a deliberate cast, because the failure is otherwise silent: a broken
 * image, no error, nothing in the console.
 *
 * A doc comment would not have stopped this - the 2026-09-06 lesson is that a warning sitting
 * in prose next to a wrong code path protects nothing. So the type refuses instead.
 */
export type UnsignedBlobPath = string & { readonly __brand: 'unsigned blob path' };

/**
 * `GET /api/agency` returns branding FLAT, not as a nested object - confirmed live 2026-09-06.
 * `GET /api/agency/branding` returns the nested `Branding` above. The two are not the same
 * shape and a client that assumes they are gets `undefined`.
 *
 * **And `brandingLogoUrl` is not a URL** - confirmed live 2026-09-07. It is the raw blob path
 * (`"<agencyId>/logo.png"`), with no SAS token, fetchable by nothing. Only
 * `GET /api/agency/branding` mints a signed `logoUrl`. Any logo a person will actually see must
 * come from `getBranding()`.
 */
export interface Agency {
  readonly id: string;
  readonly name: string;
  readonly mode: AgencyMode;
  readonly timeZoneId: string;
  readonly brandingDisplayName: string | null;
  /** NOT a URL - see above. Use `getBranding().logoUrl` for anything renderable. */
  readonly brandingLogoUrl: UnsignedBlobPath | null;
  readonly brandingAccentColor: string | null;
}

export interface Client {
  readonly id: string;
  readonly name: string;
  /** True for the "self" client a Direct-mode agency gets at signup. Not in the reference. */
  readonly isSelf: boolean;
  readonly portalAccessDisabled: boolean;
  readonly createdAt: string;
}

export interface Agent {
  readonly id: string;
  readonly name: string;
  readonly externalAgentId: string;
  readonly syncEnabled: boolean;
}

/**
 * What `POST /api/connections` returns: the connection, plus the agents the supplied key
 * actually reached at the provider.
 *
 * The agent list is the point. A client cannot honestly say what a first sync will pull until
 * somebody has asked the provider, and before 2026-09-07 nothing had - the key was stored
 * unchecked, so the onboarding flow's "Verifying API key" tick was drawn on a timer and any
 * stated volume was invented. Additive: `ProviderConnection` is unchanged.
 */
/**
 * How far a sync in flight has got (2026-10-20). `Queued`: asked for, not picked up yet.
 * `Finding`: listing the provider's calls - `found` counts the NEW ones as they turn up.
 * `Downloading`: `done` of `found` are saved, and already visible in the call list.
 */
export interface SyncProgress {
  readonly phase: 'Queued' | 'Finding' | 'Downloading' | (string & {});
  readonly found: number | null;
  readonly done: number | null;
}

/** `GET /api/connections/{id}/sync` - what the app polls while a sync runs. */
export interface ConnectionSyncState {
  readonly inProgress: boolean;
  readonly progress: SyncProgress | null;
  readonly syncStatus: SyncStatus;
  readonly lastSyncedAt: string | null;
}

export interface CreatedConnection extends ProviderConnection {
  readonly agents: readonly DiscoveredAgent[];
}

/** An agent as the provider reports it, before Fig has a row for it. No Fig id yet. */
export interface DiscoveredAgent {
  readonly externalAgentId: string;
  readonly name: string;
}

export interface ProviderConnection {
  readonly id: string;
  readonly clientId: string;
  readonly providerType: string;
  readonly connectionName: string;
  readonly syncSchedule: SyncSchedule;
  readonly syncStatus: SyncStatus;
  readonly lastSyncedAt: string | null;
  /** A sync running right now. The natural driver of a "Syncing..." state. */
  readonly syncInProgress: boolean;
  /** Present while a sync is in flight, on servers from 2026-10-20. Absent on older ones. */
  readonly progress?: SyncProgress | null;
  /**
   * Whether a sync has ever actually RUN — which is NOT the same question as whether
   * `lastSyncedAt` has a value, and the difference cost a real device report.
   *
   * On 2026-09-26 the app showed "Last updated 7:16 am" against day-old calls and a refresh that
   * changed nothing. Both were correct: it was the demo tenant, whose `lastSyncedAt` was written by
   * the seeder and has never been a sync. **A timestamp with `hasEverSynced: false` must never be
   * rendered as freshness** — it is sample data, and saying otherwise is a true-looking statement
   * about something that did not happen.
   *
   * Derived server-side from `SyncStartedAt`, which a real sync writes first.
   */
  readonly hasEverSynced: boolean;
}

/**
 * THE LIST ROW AND THE DETAIL ARE DIFFERENT SHAPES. Confirmed live 2026-09-06; the reference
 * documents neither. A screen reading `assignmentNote` off a list row gets `undefined` with no
 * error - the exact failure this split exists to make impossible.
 */
export interface ConversationListItem {
  readonly id: string;
  readonly clientId: string;
  readonly agentId: string | null;
  /**
   * The agent's name as it is RIGHT NOW, resolved server-side through the agent row rather
   * than copied onto the call at sync time - so a rename applies to the whole history.
   */
  readonly agentName: string | null;
  /** Free text - `twilio`, `whatsapp`, and others. Render it; never switch exhaustively. */
  /**
   * **Always present.** Corrected from `string | null` on 2026-09-13: the entity declares
   * `required string Channel`, the column is NOT NULL, and both DTOs carry it non-nullable — so
   * every conversation has a source and there is no empty state to design for. Both clients had
   * been guarding against a null that cannot arrive, which is why the source was rendered
   * tentatively in one place instead of confidently everywhere.
   */
  readonly channel: string;
  readonly startedAt: string;
  readonly durationSeconds: number;
  readonly hasAudio: boolean;
  readonly sentiment: Sentiment | null;
  /** Free text from the provider - "Booking confirmed". NEVER build a fixed dropdown. */
  readonly outcome: string | null;
  /**
   * The provider's short name for the call - "Billing Transfer", "Who's Calling?". Live since
   * 2026-09-09.
   *
   * **This is the row's headline, and `summary` must never be substituted for it.** A summary
   * is a full sentence; a column of sentences is prose with dividers in it, which is precisely
   * what the v1 list looked like and what the v2 design exists to undo. When this is null, fall
   * back to `outcome`, then to "Call".
   */
  readonly title: string | null;
  /** A full sentence. Belongs in the row's quiet preview line and on the detail screen. */
  readonly summary: string | null;

  /**
   * The sentence a keyword search matched in, with each matched word wrapped in `[[` and `]]`.
   *
   * Only ever present when a search term was sent, and not on every row even then — a call can
   * match on a word Fig will not show back (a title, an outcome) and gets no snippet.
   *
   * **Render this INSTEAD OF the summary preview while searching.** The snippet is the evidence
   * the person came for; the summary is not. When it is null, render the ordinary preview —
   * never an empty slot, which reads as a statement about the call rather than about the query.
   *
   * The markers are deliberately not HTML: emphasise them by splitting, or strip them, without
   * either client having to trust markup that came out of a transcript.
   */
  readonly matchSnippet: string | null;
  /**
   * The other party's number, E.164 (`+61419802814`). Null for a text channel.
   *
   * Format it for the viewer at the point of display (`nationalNumber`), never store or
   * transmit it formatted - the national form is one region's convention and is wrong
   * everywhere else.
   */
  readonly externalNumber: string | null;
  /** `inbound` / `outbound`, open string. Null without a telephony leg. */
  readonly direction: string | null;
  readonly assignedUserId: string | null;
  readonly assignedAt: string | null;
}

/**
 * How long a share stays alive. A closed set plus a custom number of days, because the value is
 * not continuous - nobody wants thirteen days - and four named answers are what somebody is
 * actually choosing between. Rendered as chips, never a slider: a slider is imprecise for four
 * real answers and is the least accessible control available for them.
 */
export type ShareLifetime = 'Hours24' | 'Days7' | 'Days30' | 'Never' | 'Custom';

export interface CreateShareRequest {
  readonly conversationId: string;
  readonly lifetime: ShareLifetime;
  /** Only with `Custom`. 1-365; longer than that is `Never`, chosen deliberately. */
  readonly customDays?: number;
  /**
   * Whether the words travel with the audio. OFF unless the sharer turns it on - a transcript is
   * a larger disclosure than the recording, because it is searchable, quotable and skimmable by
   * anyone who opens the page.
   */
  readonly includeTranscript: boolean;
  readonly requirePasscode: boolean;
}

export interface CreatedShare {
  readonly id: string;
  /** The full link. Assembled server-side so every client sends the same one. */
  readonly url: string;
  /**
   * The plain code. The sharer passes it on by another route, which is the entire point: a code
   * that travelled with the link would defend against nothing. Also on `SharedLink` while live.
   */
  readonly passcode: string | null;
  /** Null means it never expires. */
  readonly expiresAt: string | null;
}

/**
 * One live, expired or revoked share, for the screen that answers *what is out there right now*.
 *
 * The dead ones are included deliberately: "what am I sharing" is the headline question, but
 * "did anyone ever open the one I sent in August" is the one somebody asks afterwards, and a list
 * that silently drops a link when it expires cannot answer it.
 */
export interface SharedLink {
  readonly id: string;
  readonly conversationId: string;
  readonly conversationTitle: string | null;
  readonly agentName: string | null;
  readonly conversationStartedAt: string;
  readonly clientId: string;
  readonly createdByUserId: string;
  readonly createdAt: string;
  /** Null means it never expires, and the UI says so in words rather than leaving a blank. */
  readonly expiresAt: string | null;
  readonly revokedAt: string | null;
  /**
   * How many times the page has been opened. The field that tells somebody whether a link is
   * still in use - which an expiry can only guess at - and the reason the monthly idle-link
   * review is information rather than nagging.
   */
  readonly openCount: number;
  readonly lastOpenedAt: string | null;
  readonly includesTranscript: boolean;
  readonly hasPasscode: boolean;

  /**
   * The link itself, so the sharer can copy or send it again (Rasel, 2026-09-25: "once you share
   * a recording there is no way to retrieve that link"). Present only while the link is LIVE.
   * Null on a live link means it was made before 2026-09-25, when only a hash was kept — the
   * client says so and offers a new link, rather than showing a blank.
   */
  readonly url: string | null;
  /** The code, under the same rules. Null when the link has no code. */
  readonly passcode: string | null;

  /**
   * People who pressed "ask them to send it again" on the public page and have not been dealt
   * with. This is what turns that button from a gesture into a message — without it the recipient
   * tells us and the only person who can act is never told.
   */
  readonly pendingResendRequests: number;

  /**
   * People who said the recording may be of them.
   *
   * Kept separate from the resends on purpose: one is a convenience and the other is a privacy
   * right with a legal clock on it, and a single combined "requests" badge would bury the one
   * that matters underneath the one that does not.
   */
  readonly pendingIdentityRequests: number;
}

export interface ConversationDetail extends ConversationListItem {
  readonly externalConversationId: string;
  /**
   * How the call ended, in the provider's words - "Call ended by remote party". Detail only:
   * it explains a call rather than identifying one, so it has no business in a row somebody is
   * scanning.
   */
  readonly terminationReason: string | null;
  readonly assignedByUserId: string | null;
  readonly assignmentNote: string | null;
  /**
   * ~100 amplitude peaks (0-1000), extracted server-side during sync. Three states:
   *   `null`  - never attempted (a text-only call, or synced before extraction existed)
   *   `[]`    - attempted, nothing there (a provider stub with no audio frames)
   *   peaks   - real data
   *
   * **Draw a plain progress bar for the first two.** Never a decorative shape - something that
   * looks like data and is not fails the honesty directive and the premium bar at once, which
   * is why the original "decorative waveform" recommendation was withdrawn.
   */
  readonly waveformPeaks: readonly number[] | null;
}

/** Live since 2026-09-06. See `ConversationDetail.waveformPeaks` for the three states. */
export type WaveformPeaks = readonly number[] | null;

/** True when there are peaks worth drawing. Both `null` and `[]` mean "draw a plain bar". */
export const hasWaveform = (peaks: WaveformPeaks): peaks is readonly number[] =>
  peaks !== null && peaks.length > 0;

export interface TranscriptTurn {
  readonly id: string;
  readonly role: TranscriptRole;
  readonly text: string;
  readonly offsetSeconds: number;
}

/** `GET .../transcript` returns an OBJECT, not a bare array. */
export interface Transcript {
  readonly conversationId: string;
  readonly turns: readonly TranscriptTurn[];
}

export interface AssignableUser {
  readonly id: string;
  readonly name: string;
}

export interface AgencyUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly role: UserRole;
  /** Invited but has never signed in. */
  readonly pending: boolean;
  readonly disabled: boolean;
  readonly invitedByUserId: string | null;
  readonly createdAt: string;
}

export interface ClientPortalUser {
  readonly id: string;
  readonly clientId: string;
  readonly email: string;
  readonly name: string;
  readonly pending: boolean;
  readonly disabled: boolean;
  /** The whole-client switch, separate from this person's own `disabled`. */
  readonly clientPortalAccessDisabled: boolean;
  readonly invitedByUserId: string | null;
  readonly createdAt: string;
}

export interface PortalTermsState {
  readonly currentVersion: string;
  readonly acceptedVersion: string | null;
  readonly acceptedAt: string | null;
  /** Read THIS, never compare versions yourself. */
  readonly acceptanceRequired: boolean;
}

/** A page of conversations. `nextCursor` is null on the last page. */
export interface Page<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
}

// ---------------------------------------------------------------- account deletion

/**
 * Which of the three cases a deletion falls into. **Branch on this, never on the counts** -
 * `refusedLastAdmin` comes back with the agency's real numbers precisely so the refusal can
 * explain what is at stake, and treating a non-zero count as "go ahead" would be wrong.
 */
export type DeletionScope = 'agencyAndEverything' | 'userOnly' | 'refusedLastAdmin';

export interface DeletionPreview {
  readonly scope: DeletionScope;
  readonly conversationCount: number;
  /** Not the same number as `conversationCount` - a call with no audio is still a call. */
  readonly recordingCount: number;
  readonly otherUserCount: number;
  /** Lets the screen say "3 months of history" rather than only a count. */
  readonly oldestConversationAt: string | null;
  /**
   * Sent by the server rather than hard-coded in the app, so the number on the confirmation
   * screen and the number the server will honour cannot drift apart across app versions.
   */
  readonly recoveryWindowDays: number;
}

export interface DeletionSchedule {
  readonly scope: DeletionScope;
  readonly requestedAt: string;
  /** When it happens with no further action from anybody. Never null - this date is the
   * difference between a deletion and a deactivation. */
  readonly dueAt: string;
}

/**
 * Where a data-export request has got to.
 *
 * **The complete set, written out rather than inferred**, because the screen branches on this and
 * nothing else - and because an API reference that documents endpoints but not enum VALUES is not
 * a usable contract. A client that guesses a sixth value gets a screen that renders nothing.
 */
export type DataExportStatus = 'pending' | 'building' | 'ready' | 'failed' | 'expired';

export interface DataExport {
  readonly id: string;
  readonly status: DataExportStatus;
  readonly requestedAt: string;
  readonly completedAt: string | null;
  /** When the archive is deleted. Seven days after it is built. */
  readonly expiresAt: string | null;
  readonly sizeBytes: number | null;
  readonly conversationCount: number;
  readonly recordingCount: number;
  /**
   * Present only when ready, and valid for **15 minutes from the moment the response was
   * produced**. Never cache it: a stored link to somebody's entire account is a liability we
   * would have created for them.
   */
  readonly downloadUrl: string | null;
  /** Why it failed, in words. A status with no reason is a support conversation nobody can start. */
  readonly error: string | null;
}

/**
 * What a person is telling us when they send feedback.
 *
 * **Four values and no "Other", deliberately.** A list with "Other" on it collects most of the
 * traffic and tells us nothing, and a longer list makes somebody classify their own problem before
 * they are allowed to describe it - work pushed onto them to save us a query. These four differ in
 * what we DO about them, which is the only justification for asking at all.
 */
export const FEEDBACK_KINDS = ['Problem', 'Idea', 'Confusing', 'Praise'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

/**
 * Everything except `message` is context the client reads for itself.
 *
 * **Standing rule: never ask for what the system can already know.** Version, platform, device and
 * the screen the person was on are all readable here, and a report saying "it crashed" with no
 * version attached is usually unactionable. Asking for them would be several pebbles in front of
 * somebody already annoyed enough to complain.
 */
export interface SubmitFeedbackRequest {
  readonly message: string;
  readonly kind?: FeedbackKind;
  /** The call they were looking at, so "this transcript is wrong" is investigable. */
  readonly conversationId?: string;
  readonly screen?: string;
  readonly appVersion?: string;
  readonly platform?: string;
  readonly deviceModel?: string;
}

export interface FeedbackItem {
  readonly id: string;
  readonly kind: FeedbackKind;
  readonly message: string;
  readonly submittedAt: string;
  /** Null means nobody has answered yet. The screen says so plainly rather than leaving a gap. */
  readonly reply: string | null;
  readonly repliedAt: string | null;
  /** A reply this person has not seen yet. Drives the dot, and is cleared by an explicit call. */
  readonly replyIsNew: boolean;
}
